import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import { DEVICE_TYPES, useGetDevicesBySite } from 'api/devices';
import useTenantSelection from 'hooks/useTenantSelection';

// Mirrors Weightrax.Domain.Enums.DeviceType (AppMode context)
const APP_MODE_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'All Modes' },
  ...DEVICE_TYPES.map((t) => ({ value: t.id as number | null, label: t.name }))
];

// Mirrors Weightrax.Domain.Models.LinkedDevice
export type LinkedDevice = {
  deviceId: string;
  appMode: number | null;
};

type LinkedDevicesConfigProps = {
  value: LinkedDevice[];
  onChange: (next: LinkedDevice[]) => void;
  disabled?: boolean;
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function parseAppMode(value: unknown): number | null {
  // The Local API writes enums by name, the Cloud editor by number.
  if (typeof value === 'string') {
    const byName = DEVICE_TYPES.find((t) => t.name.toLowerCase() === value.trim().toLowerCase());
    if (byName) return byName.id;
  }
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return DEVICE_TYPES.some((t) => t.id === n) ? n : null;
}

/**
 * Reads the linked devices from a peripheral configuration, accepting either casing because
 * configurations saved on a device are written in PascalCase.
 */
export function parseLinkedDevices(base: unknown): LinkedDevice[] {
  const source = asRecord(base);
  const raw = source.linkedDevices ?? source.LinkedDevices;
  if (!Array.isArray(raw)) return [];

  return raw.map((item) => {
    const rec = asRecord(item);
    const deviceId = rec.deviceId ?? rec.DeviceId;
    return {
      deviceId: typeof deviceId === 'string' ? deviceId : '',
      appMode: parseAppMode(rec.appMode ?? rec.AppMode)
    };
  });
}

/**
 * The form stored in the configuration. Rows with no device chosen are dropped: DeviceId is a Ulid
 * on the server, and an empty one would stop the whole configuration deserialising.
 */
export function linkedDevicesToJson(value: LinkedDevice[]): LinkedDevice[] {
  return value.filter((d) => d.deviceId.trim().length > 0).map((d) => ({ deviceId: d.deviceId, appMode: d.appMode }));
}

export default function LinkedDevicesConfig({ value, onChange, disabled = false }: LinkedDevicesConfigProps) {
  const { selectedCompanySiteResult } = useTenantSelection();
  const siteId = selectedCompanySiteResult?.siteId ?? null;
  const { devices } = useGetDevicesBySite(siteId);

  const updateLinkedDevice = (index: number, patch: Partial<LinkedDevice>) => {
    onChange(value.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const addLinkedDevice = () => {
    onChange([...value, { deviceId: '', appMode: null }]);
  };

  const removeLinkedDevice = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Linked Devices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Devices this peripheral is used on, optionally limited to one app mode. Leave empty to use it on all devices.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" startIcon={<IconPlus size={15} stroke={2} />} onClick={addLinkedDevice} disabled={disabled}>
          Add
        </Button>
      </Stack>

      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No linked devices, so this peripheral is used on all devices. Add one above to limit it to specific devices.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {value.map((linked, index) => {
            // A device from another site, or one since removed, is still shown so it can be seen and removed.
            const isUnknownDevice = linked.deviceId !== '' && !devices.some((d) => d.deviceId === linked.deviceId);

            return (
              <Grid container spacing={1.5} key={index} alignItems="center">
                <Grid size={{ xs: 12, md: 5 }}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Device"
                    value={linked.deviceId}
                    onChange={(e) => updateLinkedDevice(index, { deviceId: e.target.value })}
                    disabled={disabled}
                  >
                    <MenuItem value="">
                      <em>{devices.length === 0 ? 'No devices on this site' : '— Select device —'}</em>
                    </MenuItem>
                    {isUnknownDevice ? <MenuItem value={linked.deviceId}>Unknown device ({linked.deviceId})</MenuItem> : null}
                    {devices.map((d) => (
                      <MenuItem key={d.deviceId} value={d.deviceId}>
                        {d.deviceName}
                        {d.location ? ` (${d.location})` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 10, md: 3 }}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="App Mode"
                    value={linked.appMode ?? ''}
                    onChange={(e) => updateLinkedDevice(index, { appMode: e.target.value === '' ? null : Number(e.target.value) })}
                    disabled={disabled}
                  >
                    {APP_MODE_OPTIONS.map((opt) => (
                      <MenuItem key={String(opt.value)} value={opt.value ?? ''}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 'auto' }}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeLinkedDevice(index)}
                    disabled={disabled}
                    title="Remove linked device"
                  >
                    <IconTrash size={16} stroke={1.5} />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
